// ห้องแชทล่าสุด 10 ห้อง — กดแล้วเปิดลิ้นชักแชทด้านขวาเพื่อตอบได้ทันที
import { useQuery } from "@tanstack/react-query";
import type { Brand } from "@contracts/api";
import { wazzupToken } from "@features/inbox/api/inbox-api";
import { fmtDuration, fmtInt, timeAgo } from "@shared/lib/format";
import { cn } from "@shared/lib/utils";
import { fbPageIds, fetchConversations, overviewKeys } from "../api/overview-api";
import type { InboxConversation } from "../api/upstream-types";
import { OvNote } from "../components/ov-states";

const DAY = 86400000;

export function RoomsBlock({
  brand,
  onOpen,
}: {
  brand: Brand;
  onOpen: (c: InboxConversation) => void;
}) {
  const ids = fbPageIds(brand);
  const hasToken = !!wazzupToken();
  const { data, isPending } = useQuery({
    queryKey: overviewKeys.conversations(ids),
    queryFn: () => fetchConversations(ids),
    enabled: hasToken && ids.length > 0,
  });

  // บล็อก Inbox ด้านบนบอกสาเหตุไปแล้ว ไม่ต้องบอกซ้ำ
  if (!hasToken || !ids.length) return null;
  if (isPending) return <div className="ov-loading">กำลังโหลด…</div>;

  const items = data ?? [];
  if (!items.length) return <OvNote>ยังไม่มีห้องแชท</OvNote>;

  return (
    <div className="ov-rooms-grid">
      {items.map((c) => (
        <button key={c.id} type="button" className="ov-room" onClick={() => onOpen(c)}>
          <Avatar c={c} />
          <span className="grow">
            <span className="ov-room-top">
              <b>{c.customerName || "ไม่ทราบชื่อ"}</b>
              <span className="ov-room-time">{timeAgo(c.updatedTime)}</span>
            </span>
            <span className="ov-room-prev muted">
              {c.preview?.isFromPage ? "คุณ: " : ""}
              {(c.preview?.text || c.lastCustomerText || "(ไม่มีข้อความ)").slice(0, 70)}
            </span>
            <span className="ov-room-foot">
              <ReplyBubble c={c} />
              <span className="ov-room-page muted">{c.pageName || ""}</span>
            </span>
          </span>
          {c.unreadCount ? <span className="ov-room-badge">{fmtInt(c.unreadCount)}</span> : null}
        </button>
      ))}
    </div>
  );
}

function Avatar({ c }: { c: InboxConversation }) {
  const initial = (c.customerName || "?").trim().charAt(0);
  return (
    <span className="ov-room-ava">
      {c.customerPic ? (
        <img src={c.customerPic} alt="" loading="lazy" onError={(e) => e.currentTarget.remove()} />
      ) : (
        initial
      )}
    </span>
  );
}

/**
 * bubble เวลาตอบ — เกณฑ์: ไม่ถึง 1 วัน เขียว · 1–3 วัน เหลือง · เกิน 3 วัน แดง
 * ห้องที่ยังไม่ตอบใช้เกณฑ์เดียวกันกับเวลาที่รอมาแล้ว เพราะสิ่งที่ต้องรีบคือห้องที่รอนาน
 */
function ReplyBubble({ c }: { c: InboxConversation }) {
  const answered = c.replyMs != null;
  const ms = answered ? c.replyMs : c.waitingMs;
  if (ms == null) return null;
  const tone = ms < DAY ? "ok" : ms < 3 * DAY ? "warn" : "bad";
  return (
    <span className={cn("ov-bubble", tone)} title={answered ? "ใช้เวลาตอบ" : "รอตอบมาแล้ว"}>
      {answered ? "ตอบใน" : "⏳ รอ"} {fmtDuration(ms)}
    </span>
  );
}
